import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { BotaoFechar } from "../components/ui/BotaoFechar";
import { useIdioma } from "../i18n/IdiomaProvider";
import styles from "./TourSistema.module.css";

type Placement = "center" | "right" | "left" | "top" | "bottom";

interface TourStep {
  id: string;
  title: string;
  text: string;
  route?: string;
  selector?: string;
  placement?: Placement;
  keepMenuOpen?: boolean;
  events?: string[];
}

interface TourSistemaProps {
  menuAberto: boolean;
  onAbrirMenu: () => void;
  onFecharMenu: () => void;
  restartToken: number;
  tourAtivo?: boolean;
}

const TOUR_STORAGE_KEY = "systemTourCompleted";
const CARD_WIDTH = 360;
const MARGEM = 16;
const EVENTOS_FECHAR = [
  "system-tour-close-profile",
  "system-tour-close-notifications",
  "system-tour-close-period",
  "system-tour-close-empresa-filter",
  "system-tour-close-import",
  "system-tour-close-empresa-details",
  "system-tour-close-print-columns",
  "system-tour-close-empresa-modal",
];

const PASSOS: TourStep[] = [
  {
    id: "welcome",
    route: "/",
    placement: "center",
    title: "Bem-vindo ao Sistema!",
    text: "Preparamos um tour guiado para apresentar os principais recursos da plataforma.",
  },
  {
    id: "sidebar",
    route: "/",
    selector: '[data-tour="sidebar-menu"]',
    placement: "right",
    keepMenuOpen: true,
    title: "Menu de navegação",
    text: "A sidebar reúne os módulos liberados para o seu perfil. O restante da tela fica desfocado para manter o foco em cada explicação.",
  },
  {
    id: "sidebar-company",
    route: "/",
    selector: '[data-tour="sidebar-company"]',
    placement: "right",
    keepMenuOpen: true,
    title: "Empresa atual",
    text: "Neste card você confere a empresa ativa e abre o seletor para mudar de empresa.",
  },
  {
    id: "sidebar-toggle",
    route: "/",
    selector: '[data-tour="sidebar-toggle"]',
    placement: "right",
    keepMenuOpen: true,
    title: "Abrir e fechar a sidebar",
    text: "Use este botão para recolher ou expandir o menu sempre que precisar de mais espaço.",
  },
  {
    id: "header-online",
    route: "/",
    selector: '[data-tour="header-online"]',
    placement: "bottom",
    title: "Pessoas conectadas",
    text: "Os avatares mostram quem está conectado à plataforma neste momento.",
  },
  {
    id: "header-search",
    route: "/",
    selector: '[data-tour="header-search"]',
    placement: "bottom",
    title: "Pesquisa de menus",
    text: "Pesquise uma tela pelo nome e acesse rapidamente qualquer módulo disponível.",
  },
  {
    id: "header-date",
    route: "/",
    selector: '[data-tour="filtro-data"]',
    placement: "bottom",
    title: "Filtro de datas",
    text: "Defina o intervalo usado pelos indicadores e pelas informações da visão geral.",
  },
  {
    id: "header-ai",
    route: "/",
    selector: '[data-tour="header-ai"]',
    placement: "bottom",
    title: "Agente de IA",
    text: "Quando habilitado, o agente de IA oferece suporte e ajuda durante o uso da plataforma.",
  },
  {
    id: "header-notifications",
    route: "/",
    selector: '[data-tour="header-notifications"]',
    placement: "bottom",
    title: "Notificações",
    text: "Este sino abre a central com atividades, novidades e avisos da plataforma.",
  },
  {
    id: "header-profile",
    route: "/",
    selector: '[data-tour="header-profile"]',
    placement: "bottom",
    title: "Perfil e acessos",
    text: "Aqui você abre o perfil do login e encontra preferências, idioma, status e acessos pessoais.",
  },
  {
    id: "profile-panel",
    route: "/",
    selector: '[data-tour="profile-panel"]',
    placement: "left",
    events: ["system-tour-open-profile"],
    title: "Menu do perfil",
    text: "Neste painel você ajusta tema, status e idioma, consulta sua conta e acessa as opções disponíveis para o seu perfil.",
  },
  {
    id: "notifications-panel",
    route: "/",
    selector: '[data-tour="notifications-panel"]',
    placement: "left",
    events: ["system-tour-open-notifications"],
    title: "Central de notificações",
    text: "Pesquise alertas, filtre itens não lidos ou do dia, alterne entre atividades, novidades e avisos e marque tudo como lido.",
  },
  {
    id: "period-panel",
    route: "/",
    selector: '[data-tour="period-panel"]',
    placement: "left",
    events: ["system-tour-open-period"],
    title: "Intervalo temporal",
    text: "Escolha um período rápido ou personalize as datas no calendário; depois use Filtrar para aplicar ou Limpar para remover o intervalo.",
  },
  {
    id: "companies-page",
    route: "/cadastros/empresas",
    selector: '[data-tour="cad-empresa-header"]',
    placement: "bottom",
    title: "Cadastro de empresas",
    text: "Usaremos esta listagem para mostrar o padrão dos cadastros: filtros, pesquisa, ações, seleção, detalhes e paginação.",
  },
  {
    id: "companies-filter",
    route: "/cadastros/empresas",
    selector: '[data-tour="cad-empresa-filter-panel"]',
    placement: "bottom",
    events: ["system-tour-open-empresa-filter"],
    title: "Filtrar por campo",
    text: "Escolha o campo usado na pesquisa, como ID, razão social, fantasia, CNPJ, CPF ou cidade, e adicione um período quando necessário.",
  },
  {
    id: "companies-quick-search",
    route: "/cadastros/empresas",
    selector: '[data-tour="cad-empresa-quick-search"]',
    placement: "bottom",
    title: "Pesquisa e filtros rápidos",
    text: "Digite o termo de busca e combine os chips de status e tipo para refinar a listagem imediatamente.",
  },
  {
    id: "companies-list-actions",
    route: "/cadastros/empresas",
    selector: '[data-tour="cad-empresa-list-actions"]',
    placement: "bottom",
    title: "Controles da listagem",
    text: "Atualize os dados e alterne entre as visualizações em lista e grade por estes controles.",
  },
  {
    id: "companies-top-actions",
    route: "/cadastros/empresas",
    selector: '[data-tour="cad-empresa-top-actions"]',
    placement: "left",
    title: "Ações do cadastro",
    text: "Importar carrega registros por planilha e Nova Empresa abre o formulário de inclusão.",
  },
  {
    id: "companies-import",
    route: "/cadastros/empresas",
    selector: '[data-tour="empresa-import-modal"]',
    placement: "right",
    events: ["system-tour-open-import"],
    title: "Importação por planilha",
    text: "Arraste um arquivo XLSX ou CSV, baixe o modelo esperado e confirme a importação quando o arquivo estiver pronto.",
  },
  {
    id: "companies-row-actions",
    route: "/cadastros/empresas",
    selector: '[data-tour="table-first-row"]',
    placement: "top",
    title: "Controles de cada registro",
    text: "Em cada linha você pode selecionar o registro, marcar como favorito e usar a ação de edição ou o menu disponível.",
  },
  {
    id: "companies-selection",
    route: "/cadastros/empresas",
    selector: '[data-tour="table-selection-bar"]',
    placement: "bottom",
    events: ["system-tour-select-first-company"],
    title: "Ações em massa",
    text: "Ao selecionar registros, esta barra libera visualizar, duplicar, excluir, exportar, imprimir, consultar anexos e limpar a seleção.",
  },
  {
    id: "companies-details",
    route: "/cadastros/empresas",
    selector: '[data-tour="record-details-panel"]',
    placement: "left",
    events: ["system-tour-open-company-details"],
    title: "Detalhes da empresa",
    text: "O painel lateral organiza todas as informações por seções e oferece exportação e impressão do registro.",
  },
  {
    id: "companies-print",
    route: "/cadastros/empresas",
    selector: '[data-tour="print-columns-modal"]',
    placement: "right",
    events: ["system-tour-open-print-columns"],
    title: "Colunas do relatório",
    text: "Antes de imprimir, escolha exatamente quais colunas devem aparecer no relatório ou mantenha a impressão completa.",
  },
  {
    id: "companies-pagination",
    route: "/cadastros/empresas",
    selector: '[data-tour="table-pagination"]',
    placement: "top",
    events: ["system-tour-clear-company-selection"],
    title: "Paginação e quantidade de linhas",
    text: "A listagem começa com 10 itens. Escolha 25, 50 ou 100 por página e navegue pelas páginas para carregar os próximos registros.",
  },
];

function clamp(valor: number, minimo: number, maximo: number) {
  return Math.min(Math.max(valor, minimo), maximo);
}

function esperar(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function disparar(nome: string) {
  window.dispatchEvent(new Event(nome));
}

export function TourSistema({
  menuAberto,
  onAbrirMenu,
  onFecharMenu: _onFecharMenu,
  restartToken,
  tourAtivo = true,
}: TourSistemaProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { traduzir } = useIdioma();
  const [indice, setIndice] = useState<number | null>(null);
  const [ancora, setAncora] = useState<DOMRect | null>(null);
  const autoIniciadoRef = useRef(false);
  const passoEntradoRef = useRef("");
  const passoAtual = indice == null ? null : PASSOS[indice];

  useEffect(() => {
    if (restartToken <= 0) return;
    localStorage.removeItem(TOUR_STORAGE_KEY);
    passoEntradoRef.current = "";
    setIndice(0);
  }, [restartToken]);

  useEffect(() => {
    if (!tourAtivo || autoIniciadoRef.current || location.pathname !== "/") return;
    if (localStorage.getItem(TOUR_STORAGE_KEY) === "true") return;
    autoIniciadoRef.current = true;
    const timer = window.setTimeout(() => {
      passoEntradoRef.current = "";
      setIndice(0);
    }, 650);
    return () => window.clearTimeout(timer);
  }, [location.pathname, tourAtivo]);

  useEffect(() => {
    if (!passoAtual) return;
    if (passoAtual.route && location.pathname !== passoAtual.route) {
      navigate(passoAtual.route);
      return;
    }
    if (passoAtual.keepMenuOpen && !menuAberto) onAbrirMenu();
    if (passoEntradoRef.current === passoAtual.id) return;
    passoEntradoRef.current = passoAtual.id;
    EVENTOS_FECHAR.forEach(disparar);
    passoAtual.events?.forEach(disparar);
    void (async () => {
      if (!passoAtual.selector) return;
      await esperar(320);
      const alvo = document.querySelector(passoAtual.selector);
      if (alvo instanceof HTMLElement) {
        alvo.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      }
    })();
  }, [location.pathname, menuAberto, navigate, onAbrirMenu, passoAtual]);

  useEffect(() => {
    if (!passoAtual?.selector) {
      setAncora(null);
      return;
    }
    let frame = 0;
    const atualizar = () => {
      const alvo = document.querySelector(passoAtual.selector!);
      setAncora(alvo instanceof HTMLElement ? alvo.getBoundingClientRect() : null);
      frame = window.requestAnimationFrame(atualizar);
    };
    atualizar();
    return () => window.cancelAnimationFrame(frame);
  }, [passoAtual]);

  const estiloCard = useMemo(() => {
    if (!passoAtual || passoAtual.placement === "center" || !ancora) return {};
    const larguraTela = window.innerWidth;
    const alturaTela = window.innerHeight;
    const alturaAproximada = 260;
    let top = alturaTela / 2 - alturaAproximada / 2;
    let left = larguraTela / 2 - CARD_WIDTH / 2;
    if (passoAtual.placement === "right") {
      left = ancora.right + 24;
      top = ancora.top + ancora.height / 2 - alturaAproximada / 2;
    } else if (passoAtual.placement === "left") {
      left = ancora.left - CARD_WIDTH - 24;
      top = ancora.top + ancora.height / 2 - alturaAproximada / 2;
    } else if (passoAtual.placement === "top") {
      left = ancora.left + ancora.width / 2 - CARD_WIDTH / 2;
      top = ancora.top - alturaAproximada - 24;
    } else if (passoAtual.placement === "bottom") {
      left = ancora.left + ancora.width / 2 - CARD_WIDTH / 2;
      top = ancora.bottom + 24;
    }
    return {
      top: clamp(top, MARGEM, alturaTela - alturaAproximada - MARGEM),
      left: clamp(left, MARGEM, larguraTela - CARD_WIDTH - MARGEM),
    };
  }, [ancora, passoAtual]);

  if (indice == null || !passoAtual || typeof document === "undefined") return null;

  function concluir(recarregarPagina = false) {
    localStorage.setItem(TOUR_STORAGE_KEY, "true");
    passoEntradoRef.current = "";
    setIndice(null);
    EVENTOS_FECHAR.forEach(disparar);
    disparar("system-tour-clear-company-selection");
    onAbrirMenu();
    if (recarregarPagina) {
      window.setTimeout(() => window.location.reload(), 60);
    }
  }

  function proximo() {
    if (indice == null) return;
    if (indice >= PASSOS.length - 1) return concluir(true);
    passoEntradoRef.current = "";
    setIndice(indice + 1);
  }

  function voltar() {
    if (indice == null) return;
    passoEntradoRef.current = "";
    setIndice(Math.max(0, indice - 1));
  }

  const primeiro = indice === 0;
  const ultimo = indice === PASSOS.length - 1;
  const foco = ancora && passoAtual.placement !== "center"
    ? {
        top: clamp(ancora.top - 8, 0, window.innerHeight),
        left: clamp(ancora.left - 8, 0, window.innerWidth),
        right: clamp(ancora.right + 8, 0, window.innerWidth),
        bottom: clamp(ancora.bottom + 8, 0, window.innerHeight),
      }
    : null;

  return createPortal(
    <div className={styles.overlay}>
      {foco ? (
        <>
          <div className={styles.glass} style={{ top: 0, left: 0, right: 0, height: foco.top }} />
          <div className={styles.glass} style={{ top: foco.top, left: 0, width: foco.left, height: Math.max(0, foco.bottom - foco.top) }} />
          <div className={styles.glass} style={{ top: foco.top, left: foco.right, right: 0, height: Math.max(0, foco.bottom - foco.top) }} />
          <div className={styles.glass} style={{ top: foco.bottom, left: 0, right: 0, bottom: 0 }} />
          <div className={styles.spotlight} style={{ top: foco.top, left: foco.left, width: Math.max(0, foco.right - foco.left), height: Math.max(0, foco.bottom - foco.top) }} />
        </>
      ) : (
        <div className={`${styles.glass} ${styles.glassFull}`} />
      )}

      <div className={`${styles.card} ${passoAtual.placement === "center" || !ancora ? styles.center : ""}`} style={estiloCard}>
        <BotaoFechar className={styles.fechar} aria-label={traduzir("Fechar tour")} onClick={() => concluir()} />
        <span className={styles.progresso}>{indice + 1} / {PASSOS.length}</span>
        <h3 className={styles.titulo}>{traduzir(passoAtual.title)}</h3>
        <p className={styles.texto}>{traduzir(passoAtual.text)}</p>
        <div className={styles.acoes}>
          {primeiro ? (
            <>
              <button type="button" className={styles.botaoSecundario} onClick={() => concluir()}>{traduzir("Pular")}</button>
              <button type="button" className={styles.botaoPrimario} onClick={proximo}>{traduzir("Começar")}</button>
            </>
          ) : (
            <>
              <button type="button" className={styles.botaoSecundario} onClick={voltar}>{traduzir("Voltar")}</button>
              <button type="button" className={styles.botaoPrimario} onClick={ultimo ? () => concluir(true) : proximo}>{traduzir(ultimo ? "Finalizar" : "Próximo")}</button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
